// apps/embed/src/chat-widget/ChatEmailForm.tsx
//
// The website chat widget's EMAIL-ONLY body — shown when a business runs neither
// the AI bot nor live human chat, but does have a public e-mail. The
// visitor leaves a message (name / email / phone / message via the SAME shared
// PreChatForm as live chat) which is emailed to the business through the existing
// /contact-requests endpoint. No chat session, no polling.

import { useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { TFunc, AppLocale } from '@tourtask/types/i18n';
import type { CountryWithName } from '@tourtask/types/configs/Country';
import { CONTACT_REQUEST_MAX_MESSAGE_LENGTH } from '@tourtask/constants/inputs/contact';
import { PreChatForm } from '@tourtask/ui/components/chat/PreChatForm';
import { sendContactRequest } from '@tourtask/storefront/api/sendContactRequest';
import { useContactRequestProtection } from '@tourtask/storefront/hooks/useContactRequestProtection';
import { ContactHoneypot } from '@tourtask/ui/primitives/forms/ContactHoneypot';

interface ChatEmailFormProps {
  bizSlug: string;
  bizToken: string;
  t: TFunc;
  langCode: string;
  countries: CountryWithName[];
}

export function ChatEmailForm({
  bizSlug,
  bizToken,
  t,
  langCode,
  countries,
}: ChatEmailFormProps) {
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const proof = useContactRequestProtection(bizSlug, bizToken);
  const honeypot = useRef<HTMLInputElement>(null);

  if (sent) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          p: 3,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600, textAlign: 'center' }}>
          {t('Message_sent')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
          {t('CONTACT_US_RECEIVED_INFO')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
      <PreChatForm
        formFields={<ContactHoneypot inputRef={honeypot} />}
        t={t}
        countriesWithNames={countries}
        starting={sending}
        // Send-failure feedback is rendered below (PreChatForm's built-in error
        // line is chat-specific), so keep its internal error hidden.
        error={null}
        introKey="Leave_a_message_INFO"
        submitKey="Send"
        // Match the server cap (/contact-requests) so an over-length message is
        // caught client-side, not rejected opaquely by the server.
        messageMaxLength={CONTACT_REQUEST_MAX_MESSAGE_LENGTH}
        onStart={(v) => {
          setSending(true);
          setError(null);
          void proof.take(honeypot.current?.value ?? '').then(async (protection) => {
            if (protection === null) {
              setSending(false);
              setError(t('Your_message_could_not_be_sent'));
              return;
            }
            const res = await sendContactRequest({
              bizSlug,
              bizToken,
              protection,
              customer: {
                name: v.name,
                email: v.email,
                phone: { countryCode: v.phoneCountryCode, number: v.phoneNumber },
                message: v.firstMessage,
                locale: langCode as AppLocale,
              },
            });
            setSending(false);
            if (res.success) setSent(true);
            else setError(res.error.message);
          });
        }}
      />
      {error && (
        <Typography color="error" variant="body2" sx={{ px: 2, pb: 2 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
}
